import { useCallback, useMemo, useRef, useState } from 'react';
import { useProjectAssetLifecyclePolicy } from '@frameio/content-security';
import { cacheModify } from '@frameio/graphql-client';
import { number, plural } from '@frameio/i18n';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { useLoadingToast } from '@frameio/shared/resources';
import { AlertDialog, AlertDialogButton, Text, theme } from '@frameio/vapor';
import type { AssetThumbnailFragment } from '../../shared/gql/generated/AssetThumbnail';
import { useResetAssetsLifecycleMutation } from '../gql/generated/ResetAssetsLifecycle';
import TEXT from './generated/TEXT/useResetAssetsLifecycle';

export type ResetAssetLifecycleReturn = {
  resetAssetsLifecycle: (assetIds: string[]) => void;
  confirmResetAssetsLifecycleDialog: React.ReactNode;
};

/**
 * Hook that provides a function to reset the lifecycle of multiple
 * assets by clearing their archiveFrom date.
 *
 * @note This hook is meant to become a bulk operation in phase 2.
 *
 * @returns A callback function that accepts an array of asset IDs and
 * resets their lifecycle by setting archiveFrom to the current date.
 * The function also updates the Apollo cache to reflect the changes.
 */
export const useResetAssetsLifecycle = (): ResetAssetLifecycleReturn => {
  const [resetAssetsLifecycle] = useResetAssetsLifecycleMutation({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const resetAssetIds = useRef<string[]>([]);
  const { addToast, updateAndHideToast } = useLoadingToast();
  const projectId = useActiveProjectId();
  const projectPolicy = useProjectAssetLifecyclePolicy(projectId);

  const resetLifecycle = useCallback(
    async (assetIds: string[]) => {
      return resetAssetsLifecycle({
        variables: {
          input: {
            assetIds,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          resetArchiveFromForAssets: {
            __typename: 'ResetArchiveFromForAssetsPayload',
            assets: {
              __typename: 'AssetConnection',
              totalCount: assetIds.length,
            },
          },
        },
        update: (cache) => {
          assetIds.forEach((assetId) => {
            // @todo (FIND-2048): Ideally we would want to use a value from the
            // server, but as is the mutation responds with the previous value.
            // I am just using the current date for now as its close enough.
            cacheModify<AssetThumbnailFragment>(cache, {
              id: cache.identify({
                __typename: 'Asset',
                id: assetId,
              }),
              fields: {
                archiveFrom: () => new Date().toISOString(),
              },
            });
          });
        },
      });
    },
    [resetAssetsLifecycle]
  );

  const confirmResetAssetsLifecycle = useCallback((assetIds: string[]) => {
    resetAssetIds.current = assetIds;
    setShowConfirmation(true);
  }, []);

  const assetIds = resetAssetIds.current;
  const toastPendingText = TEXT.toast.pending(assetIds.length) as string;
  const toastSuccessText = TEXT.toast.success(assetIds.length) as string;
  const toastErrorText = TEXT.toast.error(assetIds.length) as string;

  const handleResetAssetsLifecycle = useCallback(async () => {
    const toastId = addToast(toastPendingText);

    try {
      setShowConfirmation(false);

      await resetLifecycle(assetIds);

      updateAndHideToast(toastId, {
        variant: 'success',
        title: toastSuccessText,
      });
    } catch (e) {
      updateAndHideToast(toastId, {
        variant: 'error',
        title: toastErrorText,
      });
    }
  }, [
    addToast,
    assetIds,
    resetLifecycle,
    toastErrorText,
    toastPendingText,
    toastSuccessText,
    updateAndHideToast,
  ]);

  const dismissRef = useRef(null);
  const confirmDialogConfirmText = TEXT.confirmDialogConfirm;
  const confirmDialogCancelText = TEXT.confirmDialogCancel;
  const confirmDialogTitleText = TEXT.confirmDialogTitle;
  const daysBeforeAssetAutoDeletion =
    projectPolicy.workspace.daysBeforeAssetAutoDeletion ?? 0;
  const rescheduleDeletionDetailsText = TEXT.rescheduleDeletionDetails(
    daysBeforeAssetAutoDeletion
  );
  const confirmResetDialog = useMemo(
    () => (
      <AlertDialog
        actions={
          <>
            <AlertDialogButton
              ref={dismissRef}
              onClick={() => {
                setShowConfirmation(false);
              }}
            >
              {confirmDialogCancelText}
            </AlertDialogButton>
            <AlertDialogButton
              autoFocus={true}
              color="primary"
              data-testid="delete-asset-button"
              onClick={handleResetAssetsLifecycle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleResetAssetsLifecycle();
                }
              }}
            >
              {confirmDialogConfirmText}
            </AlertDialogButton>
          </>
        }
        details={
          <Text
            style={{
              paddingBottom: theme.spaces[16],
              fontSize: theme.fontSizes[70],
            }}
          >
            {rescheduleDeletionDetailsText}
          </Text>
        }
        isShowing={showConfirmation}
        leastDestructiveRef={dismissRef}
        onExited={() => {
          resetAssetIds.current = [];
        }}
        onRequestClose={() => {
          setShowConfirmation(false);
        }}
        title={confirmDialogTitleText}
        variant="notice"
      />
    ),
    [
      confirmDialogCancelText,
      handleResetAssetsLifecycle,
      confirmDialogConfirmText,
      rescheduleDeletionDetailsText,
      showConfirmation,
      confirmDialogTitleText,
    ]
  );

  return {
    resetAssetsLifecycle: confirmResetAssetsLifecycle,
    confirmResetAssetsLifecycleDialog: confirmResetDialog,
  };
};

TEXT.DEFINE({
  confirmDialogCancel: 'Cancel',
  confirmDialogConfirm: 'Reset',
  confirmDialogTitle: 'Reset Lifecycle?',
  /** Body copy for reset lifecycle confirmation: days until auto-deletion */
  rescheduleDeletionDetails: (days: number) =>
    `This will reschedule these items to delete in ${number(days)} ${plural(
      days,
      'days',
      'day'
    )}.`,
  toast: {
    error: (count: number) =>
      plural(count, {
        one: 'Failed to reset asset Lifecycle',
        other: 'Failed to reset assets Lifecycle',
      }),
    pending: (count: number) =>
      plural(count, {
        one: 'Resetting asset Lifecycle...',
        other: 'Resetting assets Lifecycle...',
      }),
    success: (count: number) =>
      plural(count, {
        one: 'Asset Lifecycle has been reset',
        other: 'Assets Lifecycle has been reset',
      }),
  },
});
